// models/Conversation.js

class Conversation {
    constructor(data = {}) {
      this.id = data.id || data._id || null;
      this._id = data._id || data.id || null;
      this.conversationId = data.conversationId || '';
      this.type = data.type || 'direct'; // direct, group
      this.name = data.name || '';
      this.avatar = data.avatar || null;
      this.participants = data.participants || [];
      this.lastMessage = data.lastMessage || '';
      this.lastMessageSender = data.lastMessageSender || null;
      this.lastMessageTime = data.lastMessageTime || null;
      this.unreadCount = data.unreadCount || 0;
      this.userId = data.userId || null; // Other participant's ID in direct chat
      this.memberCount = data.memberCount || 0;
      this.isArchived = data.isArchived || false;
      this.isMuted = data.isMuted || false;
      this.isOnline = data.isOnline || false;
      this.createdAt = data.createdAt || new Date();
      this.updatedAt = data.updatedAt || null;
    }
  
    // Validation
    validate() {
      const errors = [];
  
      if (!this.name?.trim()) {
        errors.push('Conversation name is required');
      }
  
      if (this.type === 'group' && this.participants.length < 2) {
        errors.push('Group must have at least 2 participants');
      }
  
      if (!['direct', 'group'].includes(this.type)) {
        errors.push('Invalid conversation type');
      }
  
      return {
        isValid: errors.length === 0,
        errors
      };
    }
  
    // Convert to JSON for API
    toJSON() {
      return {
        id: this.id,
        type: this.type,
        name: this.name,
        avatar: this.avatar,
        participants: this.participants,
        isArchived: this.isArchived,
        isMuted: this.isMuted
      };
    }
  
    // Create from API response
    static fromJSON(json) {
      return new Conversation({
        id: json._id || json.id,
        conversationId: json.conversationId,
        type: json.type,
        name: json.name,
        avatar: json.avatar,
        participants: json.participants || [],
        lastMessage: json.lastMessage,
        lastMessageSender: json.lastMessageSender,
        lastMessageTime: json.lastMessageTime,
        unreadCount: json.unreadCount || 0,
        userId: json.userId,
        memberCount: json.memberCount || json.participants?.length || 0,
        isArchived: json.isArchived,
        isMuted: json.isMuted,
        isOnline: json.isOnline,
        createdAt: json.createdAt,
        updatedAt: json.updatedAt
      });
    }
  
    // Check if conversation is group
    isGroup() {
      return this.type === 'group';
    }
  
    // Check if has unread messages
    hasUnread() {
      return this.unreadCount > 0;
    }
  
    // Get formatted time
    getFormattedTime() {
      if (!this.lastMessageTime) return '';
      
      const now = new Date();
      const msgDate = new Date(this.lastMessageTime);
      const diff = now - msgDate;
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);
  
      if (minutes < 1) return 'Just now';
      if (minutes < 60) return `${minutes}m ago`;
      if (hours < 24) return `${hours}h ago`;
      if (days < 7) return `${days}d ago`;
      return msgDate.toLocaleDateString();
    }
  
    // Get display name
    getDisplayName() {
      return this.name || 'Unknown';
    }
  
    // Get preview text
    getPreviewText() {
      if (!this.lastMessage) return 'No messages yet';
      
      const prefix = this.lastMessageSender ? `${this.lastMessageSender}: ` : '';
      const maxLength = 50;
      const text = this.lastMessage;
      
      if (text.length <= maxLength) return prefix + text;
      return prefix + text.substring(0, maxLength) + '...';
    }
  
    // Get participant names
    getParticipantNames() {
      if (!this.participants || this.participants.length === 0) return '';
      return this.participants.map(p => p.name || p.userId?.name).filter(Boolean).join(', ');
    }
  }
  
  export default Conversation;